import {
  type OnboardingDataField,
  type OnboardingDataFieldType,
  onboardingDataFieldTypeSchema,
} from '@gb-ticket/shared-types';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ONBOARDING_DATA_FIELD_TYPE_LABEL } from '@/products/onboarding/features/onboarding-task-field-utils';

const FIELD_TYPES = onboardingDataFieldTypeSchema.options;

type DefinitionProps = {
  mode: 'definition';
  fields: OnboardingDataField[];
  onChange: (fields: OnboardingDataField[]) => void;
  readOnly?: never;
};

type ValueProps = {
  mode: 'value';
  fields: OnboardingDataField[];
  values: Record<string, unknown>;
  onChange?: (values: Record<string, unknown>) => void;
  readOnly?: boolean;
};

export type OnboardingTaskFieldsProps = DefinitionProps | ValueProps;

export function OnboardingTaskFields(props: OnboardingTaskFieldsProps) {
  if (props.mode === 'definition') {
    return <DefinitionFields fields={props.fields} onChange={props.onChange} />;
  }
  return (
    <ValueFields
      fields={props.fields}
      values={props.values}
      onChange={props.onChange}
      readOnly={props.readOnly === true}
    />
  );
}

function DefinitionFields({
  fields,
  onChange,
}: {
  fields: OnboardingDataField[];
  onChange: (fields: OnboardingDataField[]) => void;
}) {
  function updateField(
    index: number,
    patch: Partial<OnboardingDataField> & { type?: OnboardingDataFieldType },
  ) {
    onChange(
      fields.map((field, inner) => {
        if (inner !== index) {
          return field;
        }
        if (patch.type && patch.type !== field.type) {
          return emptyField(patch.type, {
            key: patch.key ?? field.key,
            label: patch.label ?? field.label,
            required: patch.required ?? field.required,
          });
        }
        if (field.type === 'choice') {
          return { ...field, ...patch, type: 'choice' as const, options: field.options };
        }
        return emptyField(field.type, {
          key: patch.key ?? field.key,
          label: patch.label ?? field.label,
          required: patch.required ?? field.required,
        });
      }),
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Label>Dynamische Felder</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...fields, emptyField('short_text')])}
        >
          Feld hinzufügen
        </Button>
      </div>
      {fields.length === 0 ? (
        <p className="text-sm text-muted-foreground">Noch keine Felder.</p>
      ) : null}
      {fields.map((field, index) => (
        <div
          key={`${field.key}-${index}`}
          className="space-y-2 rounded-lg border border-border bg-muted/40 p-3"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Feldschlüssel</Label>
              <Input
                value={field.key}
                onChange={(event) => updateField(index, { key: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Beschriftung</Label>
              <Input
                value={field.label}
                onChange={(event) => updateField(index, { label: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Typ</Label>
              <Select
                value={field.type}
                onValueChange={(value) =>
                  updateField(index, { type: value as OnboardingDataFieldType })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {ONBOARDING_DATA_FIELD_TYPE_LABEL[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 self-end pb-2 text-sm">
              <Checkbox
                checked={field.required}
                onCheckedChange={(checked) => updateField(index, { required: checked === true })}
              />
              Pflichtfeld
            </label>
          </div>
          {field.type === 'choice' ? (
            <div className="space-y-2">
              <Label>Optionen (Wert|Label, eine je Zeile)</Label>
              <Textarea
                rows={3}
                value={field.options.map((option) => `${option.value}|${option.label}`).join('\n')}
                onChange={(event) => {
                  const options = event.target.value
                    .split('\n')
                    .map((line) => line.trim())
                    .filter(Boolean)
                    .map((line) => {
                      const [rawValue, ...rest] = line.split('|');
                      const value = rawValue?.trim() ?? '';
                      const label = rest.join('|').trim() || value;
                      return { value, label };
                    });
                  onChange(
                    fields.map((entry, inner) =>
                      inner === index && entry.type === 'choice'
                        ? { ...entry, options: options.length > 0 ? options : entry.options }
                        : entry,
                    ),
                  );
                }}
              />
            </div>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(fields.filter((_, inner) => inner !== index))}
          >
            Feld entfernen
          </Button>
        </div>
      ))}
    </div>
  );
}

function ValueFields({
  fields,
  values,
  onChange,
  readOnly,
}: {
  fields: OnboardingDataField[];
  values: Record<string, unknown>;
  onChange?: (values: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  if (fields.length === 0) {
    return null;
  }

  function setValue(key: string, value: unknown) {
    onChange?.({ ...values, [key]: value });
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {fields.map((field) => {
        const raw = values[field.key];
        return (
          <div
            key={field.key}
            className={field.type === 'long_text' ? 'space-y-2 sm:col-span-2' : 'space-y-2'}
          >
            <Label>
              {field.label}
              {field.required ? ' *' : ''}
            </Label>
            {field.type === 'short_text' ? (
              <Input
                value={typeof raw === 'string' ? raw : ''}
                disabled={readOnly}
                onChange={(event) => setValue(field.key, event.target.value)}
              />
            ) : null}
            {field.type === 'long_text' ? (
              <Textarea
                rows={3}
                value={typeof raw === 'string' ? raw : ''}
                disabled={readOnly}
                onChange={(event) => setValue(field.key, event.target.value)}
              />
            ) : null}
            {field.type === 'date' ? (
              <Input
                type="date"
                value={typeof raw === 'string' ? raw : ''}
                disabled={readOnly}
                onChange={(event) => {
                  if (!event.target.value) {
                    const next = { ...values };
                    delete next[field.key];
                    onChange?.(next);
                    return;
                  }
                  setValue(field.key, event.target.value);
                }}
              />
            ) : null}
            {field.type === 'boolean' ? (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={raw === true}
                  disabled={readOnly}
                  onCheckedChange={(checked) => setValue(field.key, checked === true)}
                />
                Ja
              </label>
            ) : null}
            {field.type === 'choice' ? (
              <Select
                value={typeof raw === 'string' && raw !== '' ? raw : undefined}
                disabled={readOnly}
                onValueChange={(value) => setValue(field.key, value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Bitte wählen" />
                </SelectTrigger>
                <SelectContent>
                  {field.options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function emptyField(
  type: OnboardingDataFieldType,
  base?: { key?: string; label?: string; required?: boolean },
): OnboardingDataField {
  const trimmedKey = base?.key?.trim();
  const trimmedLabel = base?.label?.trim();
  const resolvedKey =
    trimmedKey && trimmedKey.length > 0 ? trimmedKey : `field_${Date.now().toString(36).slice(-4)}`;
  const resolvedLabel = trimmedLabel && trimmedLabel.length > 0 ? trimmedLabel : 'Neues Feld';
  const required = base?.required ?? false;
  if (type === 'choice') {
    return {
      key: resolvedKey,
      label: resolvedLabel,
      required,
      type: 'choice',
      options: [{ value: 'option_a', label: 'Option A' }],
    };
  }
  return { key: resolvedKey, label: resolvedLabel, required, type };
}
